export interface ILazyValueInitializer<T> {
  (): T;
}

export interface ILazyValue<T> {
  readonly value: T;
  resetCache(): void;
}

export class LazyValue<T> implements ILazyValue<T> {
  private readonly _initializer: ILazyValueInitializer<T>;
  private _value: T | undefined = undefined;
  private _initialized: boolean = false;

  constructor(initializer: ILazyValueInitializer<T>) {
    this._initializer = initializer;
  }

  get value(): T {
    if (!this._initialized) {
      this._value = this._initializer();
      this._initialized = true;
    }

    return this._value as T;
  }

  resetCache() {
    this._initialized = false;
  }
}

export interface IValueObserver<T> {
  (thisValue: IObservableValue<T>, newValue: T, oldValue: T): void;
}

export interface IObservableValue<T> {
  data: T;
  valueObserver: IValueObserver<T> | undefined;
}

export class ObservableValue<T> implements IObservableValue<T> {
  private _data: T;
  valueObserver: IValueObserver<T> | undefined;

  constructor(data: T, valueObserver?: IValueObserver<T>) {
    this._data = data;
    this.valueObserver = valueObserver;
  }

  get data(): T {
    return this._data as T;
  }

  set data(value: T) {
    if (value !== this._data) {
      const oldValue = this._data as T;
      this._data = value;
      if (this.valueObserver !== undefined) {
        this.valueObserver(this, value, oldValue);
      }
    }
  }
}

export interface ITextValueProvider<T> {
  (thisValue: IDataValue<T>): string;
}

export interface ISortValueProvider<T> {
  (thisValue: IDataValue<T>): any;
}

export interface IDataValue<T> extends IObservableValue<T> {
  readonly text: string;
  readonly sortValue: T;
  resetCache(): void;
  toString(): string;
}

export class DataValue<T> implements IDataValue<T> {
  private readonly _observableValue: ObservableValue<T>;
  private readonly _textValue: LazyValue<string>;
  private readonly _sortValue: LazyValue<any>;
  textValueProvider: ITextValueProvider<T> | undefined;
  sortValueProvider: ISortValueProvider<T> | undefined;
  valueObserver: IValueObserver<T> | undefined;

  constructor(
    data: T,
    textValueProvider?: ITextValueProvider<T>,
    sortValueProvider?: ISortValueProvider<T>,
    valueObserver?: IValueObserver<T>
  ) {
    this.textValueProvider = textValueProvider;
    this.sortValueProvider = sortValueProvider;
    this.valueObserver = valueObserver;

    this._observableValue = new ObservableValue<T>(
      data,
      (thisValue, newValue, oldValue) => {
        this.resetCache();
        if (this.valueObserver !== undefined) {
          this.valueObserver(thisValue, newValue, oldValue);
        }
      }
    );

    this._textValue = new LazyValue<string>(() =>
      this.textValueProvider !== undefined
        ? this.textValueProvider(this)
        : "" + this.data
    );

    this._sortValue = new LazyValue<any>(() =>
      this.sortValueProvider !== undefined
        ? this.sortValueProvider(this)
        : this.data
    );
  }

  get data(): T {
    return this._observableValue.data;
  }

  set data(value: T) {
    this._observableValue.data = value;
  }

  get text(): string {
    return this._textValue.value;
  }

  get sortValue(): any {
    return this._sortValue.value;
  }

  resetCache() {
    this._textValue.resetCache();
    this._sortValue.resetCache();
  }

  toString(): string {
    return this.text;
  }
}
